import {
  BIA_CREATE_ITEM_STATUSES,
  BIA_EXCLUDED_BY,
  BIA_EXCLUSION_REASONS,
  BIA_GENERATE_MODES,
  BIA_IMAGE_FLAGS,
  BIA_IMAGE_QUALITIES,
  BIA_IMAGE_STATUSES,
  BIA_JOB_STATUSES,
  BIA_JOB_TYPES,
  BIA_PLAN_SOURCES,
  BIA_PRODUCT_FLAGS,
  BIA_PRODUCT_STATUSES,
  BIA_SESSION_STATUSES,
  BIA_DEFAULT_ACCEPTED_TYPES,
  BIA_DEFAULT_CLIENT_MAX_EDGE,
  BIA_DEFAULT_CREATE_CHUNK,
  BIA_DEFAULT_MAX_FILE_BYTES,
  BIA_DEFAULT_MAX_IMAGES,
  BIA_DEFAULT_MAX_INSTRUCTION_CHARS,
  BIA_DEFAULT_UPLOAD_BATCH,
  BiaConfig,
  BiaConfirmUploadsRs,
  BiaCreateItemResult,
  BiaCreateProductsRs,
  BiaError,
  BiaExclusion,
  BiaGroup,
  BiaImage,
  BiaImageNote,
  BiaImagePage,
  BiaItemError,
  BiaJobInfo,
  BiaPayloadItem,
  BiaPayloadPage,
  BiaPlan,
  BiaPlanExport,
  BiaPlanVersionInfo,
  BiaPlanWarning,
  BiaProgress,
  BiaSession,
  BiaSessionListItem,
  BiaTreeNode,
  BiaUploadUrl,
  BiaUploadUrlsRs,
} from "../../../interfaces/BulkImageAnalysisV2Object";
import { coerceSingle } from "../../helpers/ApiEnvelopeHelper";

// The Smart photos API is camelCase (Jackson defaults). These converters
// null-guard, coerce numbers and validate enum strings, so a partial or
// slightly-off response — or a value from a newer backend — can never crash a
// screen or render NaN. Unknown enum values become "UNKNOWN"; absent optional
// text becomes null; absent lists become [].

type Raw = Record<string, unknown>;

const asRecord = (value: unknown): Raw =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Raw) : {};

const toRequiredString = (value: unknown): string =>
  value === undefined || value === null ? "" : String(value);

const toNullableString = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  const text = String(value);
  return text === "" ? null : text;
};

/** Instant: ISO string, or epoch seconds / millis when a mapper writes timestamps. */
const toInstant = (value: unknown): string | null => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    const millis = value < 1e11 ? value * 1000 : value;
    return new Date(millis).toISOString();
  }
  return String(value);
};

const toCount = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

const toPositiveOr = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
};

const toNullableCount = (value: unknown): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : null;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function toArray<T>(value: unknown, convert: (raw: unknown) => T): T[] {
  return Array.isArray(value) ? value.map(convert) : [];
}

const toStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v) => v !== null && v !== undefined).map(String) : [];

/** A list payload that may arrive bare (`contents: [..]`) or wrapped (`contents: [{ items: [..] }]`). */
function listOf(raw: unknown, ...keys: string[]): unknown[] {
  if (Array.isArray(raw)) {
    // Legacy envelope: `contents: [ { items: [...] } ]` — a single wrapper object.
    if (raw.length === 1 && raw[0] && typeof raw[0] === "object" && !Array.isArray(raw[0])) {
      const only = raw[0] as Raw;
      for (const key of keys) {
        if (Array.isArray(only[key])) return only[key] as unknown[];
      }
    }
    return raw;
  }
  const rec = asRecord(raw);
  for (const key of keys) {
    if (Array.isArray(rec[key])) return rec[key] as unknown[];
  }
  return [];
}

// ── Enums ────────────────────────────────────────────────────────────────────

const toSessionStatus = (value: unknown) => oneOf(value, BIA_SESSION_STATUSES, "UNKNOWN");
const toImageStatus = (value: unknown) => oneOf(value, BIA_IMAGE_STATUSES, "UNKNOWN");
const toExclusionReason = (value: unknown) => oneOf(value, BIA_EXCLUSION_REASONS, "UNKNOWN");
const toExcludedBy = (value: unknown) => oneOf(value, BIA_EXCLUDED_BY, "UNKNOWN");
const toImageFlag = (value: unknown) => oneOf(value, BIA_IMAGE_FLAGS, "UNKNOWN");
const toQuality = (value: unknown) => oneOf(value, BIA_IMAGE_QUALITIES, "UNKNOWN");
const toJobType = (value: unknown) => oneOf(value, BIA_JOB_TYPES, "UNKNOWN");
const toJobStatus = (value: unknown) => oneOf(value, BIA_JOB_STATUSES, "UNKNOWN");
const toPlanSource = (value: unknown) => oneOf(value, BIA_PLAN_SOURCES, "UNKNOWN");
const toProductStatus = (value: unknown) => oneOf(value, BIA_PRODUCT_STATUSES, "UNKNOWN");
const toProductFlag = (value: unknown) => oneOf(value, BIA_PRODUCT_FLAGS, "UNKNOWN");
const toCreateItemStatus = (value: unknown) => oneOf(value, BIA_CREATE_ITEM_STATUSES, "UNKNOWN");

// ── Small shapes ─────────────────────────────────────────────────────────────

export function convertBiaError(raw: unknown): BiaError | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Raw;
  return {
    code: toRequiredString(r.code),
    message: toRequiredString(r.message),
    retryable: r.retryable === true,
    at: toInstant(r.at),
  };
}

export function convertItemError(raw: unknown): BiaItemError | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Raw;
  return {
    ref: toRequiredString(r.ref),
    code: toRequiredString(r.code),
    message: toRequiredString(r.message),
    retryable: r.retryable === true,
  };
}

const convertItemErrorStrict = (raw: unknown): BiaItemError =>
  convertItemError(raw) ?? { ref: "", code: "", message: "", retryable: false };

function convertProgress(raw: unknown): BiaProgress | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Raw;
  return { label: toRequiredString(r.label), done: toCount(r.done), total: toCount(r.total) };
}

function convertJob(raw: unknown): BiaJobInfo | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Raw;
  return {
    jobId: toRequiredString(r.jobId),
    type: toJobType(r.type),
    status: toJobStatus(r.status),
    attempts: toCount(r.attempts),
    startedAt: toInstant(r.startedAt),
    finishedAt: toInstant(r.finishedAt),
    error: convertBiaError(r.error),
  };
}

// ── Config ───────────────────────────────────────────────────────────────────

export function convertBiaConfig(raw: unknown): BiaConfig {
  const r = coerceSingle(raw);
  const accepted = toStringList(r.acceptedContentTypes);
  return {
    enabled: r.enabled === true,
    maxImages: toPositiveOr(r.maxImages, BIA_DEFAULT_MAX_IMAGES),
    maxFileBytes: toPositiveOr(r.maxFileBytes, BIA_DEFAULT_MAX_FILE_BYTES),
    acceptedContentTypes: accepted.length > 0 ? accepted : BIA_DEFAULT_ACCEPTED_TYPES,
    clientMaxEdgePx: toPositiveOr(r.clientMaxEdgePx, BIA_DEFAULT_CLIENT_MAX_EDGE),
    uploadBatchSize: toPositiveOr(r.uploadBatchSize, BIA_DEFAULT_UPLOAD_BATCH),
    revisionLimit: toPositiveOr(r.revisionLimit, 15),
    maxInstructionChars: toPositiveOr(r.maxInstructionChars, BIA_DEFAULT_MAX_INSTRUCTION_CHARS),
    createMaxPerCall: toPositiveOr(r.createMaxPerCall, BIA_DEFAULT_CREATE_CHUNK),
    generateMode: oneOf(r.generateMode, BIA_GENERATE_MODES, "UNKNOWN"),
    remainingImagesToday: toNullableCount(r.remainingImagesToday),
  };
}

// ── Session ──────────────────────────────────────────────────────────────────

/** True when a response body looks like a session (has an id and a status). */
export function looksLikeSession(raw: unknown): boolean {
  const r = coerceSingle(raw);
  return typeof r.sessionId === "string" && r.sessionId !== "" && typeof r.status === "string";
}

export function convertBiaSession(raw: unknown): BiaSession {
  const r = coerceSingle(raw);
  return {
    sessionId: toRequiredString(r.sessionId),
    businessId: toRequiredString(r.businessId),
    businessName: toNullableString(r.businessName),
    setupId: toNullableString(r.setupId),
    createdBy: toNullableString(r.createdBy),
    createdByName: toNullableString(r.createdByName),
    createdByAdmin: r.createdByAdmin === true,
    status: toSessionStatus(r.status),
    customerInstructions: toNullableString(r.customerInstructions),
    curatorNotes: toNullableString(r.curatorNotes),
    currentPlanVersion: toNullableCount(r.currentPlanVersion),
    confirmedPlanVersion: toNullableCount(r.confirmedPlanVersion),
    planStale: r.planStale === true,
    revisionsUsed: toCount(r.revisionsUsed),
    revisionLimit: toCount(r.revisionLimit),
    imageCount: toCount(r.imageCount),
    excludedCount: toCount(r.excludedCount),
    pendingUploadCount: toCount(r.pendingUploadCount),
    productCount: toCount(r.productCount),
    productsReady: toCount(r.productsReady),
    productsFailed: toCount(r.productsFailed),
    productsCreated: toCount(r.productsCreated),
    progress: convertProgress(r.progress),
    activeJob: convertJob(r.activeJob),
    lastJob: convertJob(r.lastJob),
    error: convertBiaError(r.error),
    createdAt: toInstant(r.createdAt),
    updatedAt: toInstant(r.updatedAt),
    expiresAt: toInstant(r.expiresAt),
  };
}

function convertSessionListItem(raw: unknown): BiaSessionListItem {
  const r = asRecord(raw);
  return {
    sessionId: toRequiredString(r.sessionId),
    businessId: toRequiredString(r.businessId),
    businessName: toNullableString(r.businessName),
    status: toSessionStatus(r.status),
    imageCount: toCount(r.imageCount),
    productCount: toCount(r.productCount),
    productsCreated: toCount(r.productsCreated),
    createdAt: toInstant(r.createdAt),
    updatedAt: toInstant(r.updatedAt),
  };
}

export function convertBiaSessionList(raw: unknown): BiaSessionListItem[] {
  return listOf(raw, "items", "sessions")
    .map(convertSessionListItem)
    .filter((item) => item.sessionId !== "");
}

// ── Images ───────────────────────────────────────────────────────────────────

function convertNote(raw: unknown): BiaImageNote | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Raw;
  const attributes: Record<string, string> = {};
  Object.entries(asRecord(r.attributes)).forEach(([key, value]) => {
    if (value !== null && value !== undefined) attributes[key] = String(value);
  });
  return {
    // Wire name is `isProduct` (BiaImageNote's @JsonProperty); tolerate `product` too.
    isProduct: r.isProduct === undefined ? r.product !== false : r.isProduct === true,
    kind: toNullableString(r.kind),
    category: toNullableString(r.category),
    attributes,
    visibleText: toNullableString(r.visibleText),
    quality: toQuality(r.quality),
    qualityNote: toNullableString(r.qualityNote),
    distinguishing: toNullableString(r.distinguishing),
  };
}

export function convertBiaImage(raw: unknown): BiaImage {
  const r = asRecord(raw);
  return {
    imageId: toRequiredString(r.imageId),
    idx: toCount(r.idx),
    fileName: toNullableString(r.fileName),
    thumbnailUrl: toNullableString(r.thumbnailUrl),
    imageUrl: toNullableString(r.imageUrl),
    widthPx: toNullableCount(r.widthPx),
    heightPx: toNullableCount(r.heightPx),
    status: toImageStatus(r.status),
    exclusionReason: r.exclusionReason === undefined || r.exclusionReason === null ? null : toExclusionReason(r.exclusionReason),
    excludedBy: r.excludedBy === undefined || r.excludedBy === null ? null : toExcludedBy(r.excludedBy),
    duplicateOf: toNullableString(r.duplicateOf),
    nearDuplicateOf: toNullableString(r.nearDuplicateOf),
    flags: toArray(r.flags, toImageFlag),
    note: convertNote(r.note),
    error: convertBiaError(r.error),
  };
}

export function convertBiaImagePage(raw: unknown): BiaImagePage {
  const r = Array.isArray(raw) ? coerceSingle(raw) : asRecord(raw);
  const items = listOf(raw, "items", "images").map(convertBiaImage).filter((image) => image.imageId !== "");
  return { items, nextCursor: toNullableString(r.nextCursor) };
}

/** A patched image: the body may be the image, or nothing useful (then null). */
export function convertBiaImageOrNull(raw: unknown): BiaImage | null {
  const r = coerceSingle(raw);
  return typeof r.imageId === "string" && r.imageId !== "" ? convertBiaImage(r) : null;
}

// ── Upload ───────────────────────────────────────────────────────────────────

function convertUploadUrl(raw: unknown): BiaUploadUrl {
  const r = asRecord(raw);
  return {
    clientRef: toRequiredString(r.clientRef),
    imageId: toRequiredString(r.imageId),
    uploadUrl: toRequiredString(r.uploadUrl),
    expiresAt: toInstant(r.expiresAt),
  };
}

export function convertBiaUploadUrls(raw: unknown): BiaUploadUrlsRs {
  const r = coerceSingle(raw);
  return {
    uploads: toArray(r.uploads, convertUploadUrl).filter((u) => u.imageId !== "" && u.uploadUrl !== ""),
    rejected: toArray(r.rejected, convertItemErrorStrict),
  };
}

export function convertBiaConfirmUploads(raw: unknown): BiaConfirmUploadsRs {
  const r = coerceSingle(raw);
  return {
    confirmed: toArray(r.confirmed, convertBiaImage).filter((image) => image.imageId !== ""),
    failed: toArray(r.failed, convertItemErrorStrict),
  };
}

// ── Plan ─────────────────────────────────────────────────────────────────────

const MAX_TREE_DEPTH_GUARD = 12;

function convertTreeNode(raw: unknown, depth = 0): BiaTreeNode {
  const r = asRecord(raw);
  return {
    key: toRequiredString(r.key),
    name: toRequiredString(r.name),
    existingSectionId: toNullableString(r.existingSectionId),
    sectionId: toNullableString(r.sectionId),
    createdByConfirm: r.createdByConfirm === true,
    productCount: toCount(r.productCount),
    children: depth >= MAX_TREE_DEPTH_GUARD ? [] : toArray(r.children, (child) => convertTreeNode(child, depth + 1)),
  };
}

function convertGroup(raw: unknown): BiaGroup {
  const r = asRecord(raw);
  return {
    key: toRequiredString(r.key),
    imageIds: toStringList(r.imageIds),
    leafKey: toNullableString(r.leafKey),
    titleHint: toNullableString(r.titleHint),
    variantOfKey: toNullableString(r.variantOfKey),
    confidence: toNullableNumber(r.confidence),
    reason: toNullableString(r.reason),
  };
}

function convertExclusion(raw: unknown): BiaExclusion {
  const r = asRecord(raw);
  return {
    imageId: toRequiredString(r.imageId),
    reason: toExclusionReason(r.reason),
    by: toExcludedBy(r.by),
    note: toNullableString(r.note),
  };
}

function convertWarning(raw: unknown): BiaPlanWarning {
  const r = asRecord(raw);
  return { code: toRequiredString(r.code), message: toRequiredString(r.message), refs: toStringList(r.refs) };
}

export function convertBiaPlan(raw: unknown): BiaPlan {
  const r = coerceSingle(raw);
  return {
    version: toCount(r.version),
    source: toPlanSource(r.source),
    basedOnVersion: toNullableCount(r.basedOnVersion),
    instruction: toNullableString(r.instruction),
    tree: toArray(r.tree, (node) => convertTreeNode(node)),
    groups: toArray(r.groups, convertGroup).filter((g) => g.key !== ""),
    excluded: toArray(r.excluded, convertExclusion).filter((e) => e.imageId !== ""),
    questions: toStringList(r.questions),
    warnings: toArray(r.warnings, convertWarning),
    summary: toNullableString(r.summary),
    confirmed: r.confirmed === true,
    createdAt: toInstant(r.createdAt),
    createdByName: toNullableString(r.createdByName),
  };
}

function convertVersionInfo(raw: unknown): BiaPlanVersionInfo {
  const r = asRecord(raw);
  return {
    version: toCount(r.version),
    source: toPlanSource(r.source),
    instruction: toNullableString(r.instruction),
    productCount: toCount(r.productCount),
    sectionCount: toCount(r.sectionCount),
    createdAt: toInstant(r.createdAt),
    createdByName: toNullableString(r.createdByName),
    current: r.current === true,
    confirmed: r.confirmed === true,
  };
}

/** Newest first. */
export function convertBiaPlanVersions(raw: unknown): BiaPlanVersionInfo[] {
  return listOf(raw, "versions", "items")
    .map(convertVersionInfo)
    .filter((v) => v.version > 0)
    .sort((a, b) => b.version - a.version);
}

export function convertBiaPlanExport(raw: unknown): BiaPlanExport {
  const r = coerceSingle(raw);
  return {
    format: r.format === "JSON" ? "JSON" : "TEXT",
    text: toNullableString(r.text),
    plan: r.plan && typeof r.plan === "object" ? convertBiaPlan(r.plan) : null,
    businessName: toNullableString(r.businessName),
    generatedAt: toInstant(r.generatedAt),
  };
}

// ── Payload and create ───────────────────────────────────────────────────────

/**
 * The wire's `product` is a CreateProductRq (`{ product: {...} }`). Unwraps it
 * so review items carry the inner Product, the same shape the existing bulk
 * flow and its preview already handle. A flat product is accepted as is.
 */
export function unwrapCreateProductRq(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const outer = raw as Raw;
  const inner = outer.product;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) return inner as Raw;
  return outer;
}

function convertPayloadItem(raw: unknown): BiaPayloadItem {
  const r = asRecord(raw);
  return {
    clientItemId: toRequiredString(r.clientItemId),
    status: toProductStatus(r.status),
    flags: toArray(r.flags, toProductFlag),
    confidence: toNullableNumber(r.confidence),
    sectionPath: toStringList(r.sectionPath),
    product: unwrapCreateProductRq(r.product),
    error: convertBiaError(r.error),
  };
}

export function convertBiaPayloadPage(raw: unknown): BiaPayloadPage {
  const r = Array.isArray(raw) ? coerceSingle(raw) : asRecord(raw);
  const items = listOf(raw, "items").map(convertPayloadItem).filter((item) => item.clientItemId !== "");
  return { items, nextCursor: toNullableString(r.nextCursor), total: toCount(r.total) || items.length };
}

function convertCreateResult(raw: unknown): BiaCreateItemResult {
  const r = asRecord(raw);
  return {
    clientItemId: toRequiredString(r.clientItemId),
    status: toCreateItemStatus(r.status),
    productId: toNullableString(r.productId),
    error: convertItemError(r.error),
  };
}

export function convertBiaCreateProducts(raw: unknown): BiaCreateProductsRs {
  const r = coerceSingle(raw);
  const results = toArray(r.results, convertCreateResult);
  return {
    results,
    createdCount: toCount(r.createdCount),
    failedCount: toCount(r.failedCount),
    sessionStatus: r.sessionStatus === undefined || r.sessionStatus === null ? null : toSessionStatus(r.sessionStatus),
  };
}

export default {
  convertBiaConfig,
  convertBiaSession,
  convertBiaSessionList,
  convertBiaImage,
  convertBiaImagePage,
  convertBiaUploadUrls,
  convertBiaConfirmUploads,
  convertBiaPlan,
  convertBiaPlanVersions,
  convertBiaPlanExport,
  convertBiaPayloadPage,
  convertBiaCreateProducts,
};
